/**
 * Generic multi-select filter dropdown.
 *
 * One component drives status, payment status, service type, vehicles and UTM
 * values. Uses the `DropdownMenuCheckboxItem` idiom already established in
 * `LeadsPage.tsx` so filter controls feel the same across the builder.
 *
 * Option lists are always passed in — never hardcoded here — because the allowed
 * values differ per resource (`bookings.service_type` permits four values,
 * `booking_quotes.service_type` only two).
 */

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { ChevronDown } from 'lucide-react'

export interface SelectOption {
  readonly value: string
  readonly label: string
}

interface Props {
  readonly label: string
  readonly options: readonly SelectOption[]
  readonly selected: readonly string[]
  readonly onChange: (next: readonly string[]) => void
  /** Shown when there are no options at all (e.g. no vehicles configured). */
  readonly emptyHint?: string
  readonly className?: string
}

export function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  emptyHint = 'No options available',
  className,
}: Props) {
  /**
   * Collapse the trigger label so a long selection can't blow out the filter
   * bar: none -> "Status", one -> "Status: Completed", many -> "Status: Completed +2".
   */
  const triggerLabel = (() => {
    if (selected.length === 0) return label
    const first = options.find((o) => o.value === selected[0])?.label ?? selected[0]
    if (selected.length === 1) return `${label}: ${first}`
    return `${label}: ${first} +${selected.length - 1}`
  })()

  const toggle = (value: string): void => {
    // Immutable — never splice the incoming array.
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={selected.length > 0 ? 'secondary' : 'outline'}
          size="sm"
          className={`gap-2 ${className ?? ''}`}
        >
          <span className="max-w-[180px] truncate">{triggerLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 w-56 overflow-y-auto">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {options.length === 0 ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">{emptyHint}</p>
        ) : (
          <>
            {options.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={selected.includes(option.value)}
                // Keep the menu open so several values can be picked in one go.
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() => toggle(option.value)}
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <div className="flex items-center justify-between px-2 py-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => onChange(options.map((o) => o.value))}
              >
                Select all
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                disabled={selected.length === 0}
                onClick={() => onChange([])}
              >
                Clear
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
